// TODO: Duplicated from /snapshots/index.tsx (2025-01)
// Refactor when:
// 1. Admin/Shared snapshot views added (third similar view), OR
// 2. Same bug needs fixing in both places 3+ times, OR
// 3. Team-specific snapshot features clearly diverge from platform
// Current differences: Team context, team quota stats, team token routing

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server.ts'
import ChevronRightIcon from '@/assets/icons/chevron-right.svg?react'
import { Button, Divider, Loading, message, Modal, Pagination, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { ItemConsumeType } from '@/consts/snapshot'
import { TeamRole } from '@/consts/team'
import { useSnapshotStorageTotal } from '@/hooks/useSnapshotStorageTotal'
import { useStorageBillingMode } from '@/hooks/useStorageBillingMode'
import { Snapshot } from '@/models/snapshot'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { SnapshotRestoreCounter } from '@/pages/snapshots/components/SnapshotRestoreCounter'
import { SnapshotStorageBadge } from '@/pages/snapshots/components/SnapshotStorageBadge'
import { useTeamStore, useTeamUserRole } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { getErrorCodeMessage } from '@/utils/error'
import {
  getBillingSwitchCompletedAt,
  SWITCH_RESUMABLE_POLL_INTERVAL_MS,
  SWITCH_RESUMABLE_POLL_WINDOW_MS,
} from '@/utils/storage-billing'

import { SnapshotFilters } from '../components/SnapshotFilters'
import { SnapshotTable } from '../components/SnapshotTable'
import { SnapshotViewSwitcher } from '../components/SnapshotViewSwitcher'
import { PAGE_SIZE } from '../constants'
import { useSnapshotOperations } from '../hooks/useSnapshotOperations'
import { SNAPSHOT_STATUS_CODES } from '../utils/statusFilters'

interface FilterItem {
  label: string
  value: string
  codes?: number[]
}

export function MemberSnapshotView() {
  const [snapList, setSnapList] = useState<Snapshot[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const navigate = useNavigate()
  const userStore = useUserStore()
  const teamStore = useTeamStore()
  const userRole = useTeamUserRole()
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const { t } = useTranslation()
  const [itemConsume, setItemConsume] = useState<{
    total: number
    used: number
  } | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // Check if user is admin or owner
  const isAdminOrOwner = userRole === TeamRole.Admin || userRole === TeamRole.Owner
  const canRecharge = userRole === TeamRole.Owner

  const teamTokenData = teamStore.teamTokenData

  const { deleteSnapshot, bulkDeleteSnapshots, shareToTeam } = useSnapshotOperations({
    teamResID: teamTokenData?.teamResID,
    onSuccess: () => {
      getSnapshotList()
      getItemConsume()
      getSnapshotStorage()
    },
  })

  const statusFilters: FilterItem[] = [
    {
      label: t('snapshot.type.available'),
      value: 'available',
      codes: [...SNAPSHOT_STATUS_CODES.available],
    },
    {
      label: t('snapshot.type.restorable'),
      value: 'restorable',
      codes: [...SNAPSHOT_STATUS_CODES.restorable],
    },
  ]
  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])

  const {
    snapshotOnSpaceBilling,
    loading: billingLoading,
    error: billingError,
  } = useStorageBillingMode()
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)

  const { snapshotStorageBytes, refresh: getSnapshotStorage } = useSnapshotStorageTotal({
    enabled: !billingLoading && !snapshotOnSpaceBilling,
    domainInfos,
  })

  const notAvailable =
    !billingLoading &&
    !billingError &&
    snapshotOnSpaceBilling &&
    !userStore.partVolume?.quota_gb &&
    snapList?.length === 0

  const getItemConsume = useCallback(async () => {
    const res = await API.getItemConsume({ type: ItemConsumeType.Snapshot })
    if (res.code === 0) {
      setItemConsume(res.info)
    }
  }, [])

  const getDomains = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(res.domainInfos || [])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const getSnapshotList = useCallback(
    async (p = page, silent = false) => {
      if (!silent) setLoading(true)
      try {
        const res = await API.getSnapList({
          domains: domainInfos.map((d) => d.domain),
          page: p,
          per_page: PAGE_SIZE,
          status: filter?.codes,
        })
        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)
          setSnapList(res.snapshots.map((snap) => new Snapshot(snap)))
        }
      } catch (error) {
        console.error(error)
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [page, filter, domainInfos],
  )

  useEffect(() => {
    const switchedAt = getBillingSwitchCompletedAt()
    if (!switchedAt || Date.now() - switchedAt >= SWITCH_RESUMABLE_POLL_WINDOW_MS) return

    const intervalId = setInterval(() => {
      if (Date.now() - switchedAt >= SWITCH_RESUMABLE_POLL_WINDOW_MS) {
        clearInterval(intervalId)
        return
      }
      getSnapshotList(page, true)
      getItemConsume()
    }, SWITCH_RESUMABLE_POLL_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [getSnapshotList, getItemConsume, page])

  const onResumeSnapshot = async (resID: string) => {
    const remainingRestores = itemConsume?.total ? itemConsume.total - (itemConsume.used || 0) : 0

    if (!snapshotOnSpaceBilling) {
      Modal.confirm({
        title: t('snapshot.restoreBillingModal.title', 'Restore Snapshot'),
        content: (
          <div className="flex flex-col gap-3">
            <div className="t-lg text-secondary">
              {t(
                'snapshot.restoreBillingModal.content',
                'This snapshot will be restored and time-based billing will resume. Please confirm to proceed.',
              )}
            </div>
            {remainingRestores <= 3 && remainingRestores > 0 && (
              <div className="t-lg text-secondary">
                {remainingRestores === 1 ? (
                  <Trans
                    i18nKey="snapshot.restoreModal.finalRestoreMessage"
                    components={{
                      1: <span className="text-error t-bold" />,
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey="snapshot.restoreModal.lowRestoreMessage"
                    values={{ count: remainingRestores }}
                    components={{
                      1: <span className="text-error t-bold" />,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        ),
        confirmBtnCfg: {
          type: 'secondary',
          text: t('snapshot.restoreBillingModal.confirmBtn', 'Restore'),
        },
        className: 'w-[640px]',
      }).then(() => {
        performRestore(resID)
      })
      return
    }

    if (remainingRestores <= 3 && remainingRestores > 0) {
      Modal.confirm({
        title: t('snapshot.restoreModal.title'),
        content: (
          <div className="flex-row">
            <div className="t-lg text-secondary">
              {remainingRestores === 1 ? (
                <Trans
                  i18nKey="snapshot.restoreModal.finalRestoreMessage"
                  components={{
                    1: <span className="text-error t-bold" />,
                  }}
                />
              ) : (
                <Trans
                  i18nKey="snapshot.restoreModal.lowRestoreMessage"
                  values={{ count: remainingRestores }}
                  components={{
                    1: <span className="text-error t-bold" />,
                  }}
                />
              )}
            </div>
          </div>
        ),
        confirmBtnCfg: {
          type: 'secondary',
          text: t('snapshot.restoreModal.confirmBtn'),
        },
        className: 'w-[640px]',
      }).then(() => {
        performRestore(resID)
      })
      return
    }

    performRestore(resID)
  }

  const performRestore = async (resID: string) => {
    setLoading(true)
    try {
      const res = await API.resumeSnapshot(
        {
          res_id: resID,
          resume_snapshot: true,
        },
        { errMsg: false },
      )
      if (res.code === 0) {
        message.success(t('snapshot.restoreSuccess', 'Restore request completed'))
        getSnapshotList()
        getItemConsume()
      } else {
        setLoading(false)
        getItemConsume()
        if (res.code === 46) {
          if (canRecharge) {
            setExchargeModalOpen(true)
          } else {
            message.error(
              t(
                'common.teamMemberInsufficientBalance',
                'Insufficient balance. Please contact your team owner to add credits.',
              ),
            )
          }
        } else if (res.code === 177) {
          Modal.confirm({
            title: t('snapshot.cannotRestoreModal.title'),
            content: (
              <div className="flex-row">
                <div className="t-lg text-secondary">
                  <Trans
                    i18nKey="snapshot.cannotRestoreModal.content"
                    components={{
                      1: <a href="mailto:support@glows.ai" className="text-highlight" />,
                    }}
                  />
                </div>
              </div>
            ),
            confirmBtnCfg: {
              type: 'secondary',
              text: t('snapshot.cannotRestoreModal.confirmBtn'),
            },
            className: 'w-[480px]',
          })
        } else if (snapshotOnSpaceBilling) {
          Modal.confirm({
            title: t('snapshot.restoreFailedModal.title'),
            content: (
              <div className="flex-row">
                <div className="t-lg text-secondary flex items-center">
                  {t('snapshot.restoreFailedModal.content')}
                </div>
              </div>
            ),
            confirmBtnCfg: {
              type: 'secondary',
              text: t('snapshot.restoreFailedModal.confirmBtn'),
            },
            className: 'w-[480px]',
          }).then(() => {
            navigate('/team/space/member')
          })
        } else {
          message.error(getErrorCodeMessage(res.code))
        }
      }
    } catch (error) {
      setLoading(false)
    }
  }

  const handleShareToTeam = async (snap: Snapshot) => {
    const ownerName = userStore.name || '-'
    shareToTeam(snap, ownerName)
  }

  useEffect(() => {
    getDomains()
    getItemConsume()
  }, [])

  useEffect(() => {
    if (domainInfos.length > 0) {
      getSnapshotList(1)
    }
  }, [domainInfos, filter])

  const handleSelectionChange = (ids: string[]) => {
    setSelectedIds(ids)
  }

  const selectedSnapshots = useMemo(
    () => snapList.filter((snap) => selectedIds.includes(snap.resID)),
    [snapList, selectedIds],
  )

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return
    await bulkDeleteSnapshots(selectedSnapshots)
    setSelectedIds([])
  }

  const handleBulkEdit = (action: string) => {
    if (action === 'delete') {
      handleBulkDelete()
    }
  }

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Snapshots List */}
      <div className="card bg-panel-white h-full overflow-y-auto">
        <div className="flex h-[60px] items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h6">
              <Trans i18nKey="snapshot.title">Snapshots</Trans>
            </div>
            {isAdminOrOwner && <SnapshotViewSwitcher />}
            <SnapshotRestoreCounter total={itemConsume?.total || 0} used={itemConsume?.used || 0} />
            {!billingLoading && !snapshotOnSpaceBilling && (
              <SnapshotStorageBadge bytes={snapshotStorageBytes} />
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isAdminOrOwner && (
              <Link to="/team/snapshots/shared">
                <Button type="outline">
                  <span className="flex items-center gap-1">
                    <Trans i18nKey="snapshot.teamTitle">Team Snapshots</Trans>
                    <ChevronRightIcon className="w-4 h-4" />
                  </span>
                </Button>
              </Link>
            )}
            <Link to="/team/create">
              <Button type="secondary">
                <Trans i18nKey="snapshot.createBtn">New Instance</Trans>
              </Button>
            </Link>
          </div>
        </div>
        <Divider />

        <SnapshotFilters
          statusFilterType="radio"
          statusOptions={statusFilters}
          selectedStatuses={filter?.value}
          onStatusChange={(value) => {
            const selectedFilter = statusFilters.find((f) => f.value === value)
            setFilter(selectedFilter)
          }}
          selectedCount={selectedIds.length}
          bulkEditActions={[
            {
              label: t('teamSnapshot.bulkEdit.delete', 'Delete'),
              value: 'delete',
              enabled: true,
              className: 't-sm text-error',
            },
          ]}
          onBulkEdit={handleBulkEdit}
        />

        {loading && (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        )}

        {!loading && !notAvailable && (
          <SnapshotTable
            snapshots={snapList}
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            variant="member"
            showCheckbox={true}
            showOwner={false}
            editableName={true}
            onNameUpdate={getSnapshotList}
            detailLinkPath={(record) => `/team/snapshots/member-detail/${record.resID}`}
            actionItems={(record) => [
              {
                label: <Trans i18nKey="teamSnapshot.admin.actionImport">Share to team</Trans>,
                onClick: () => handleShareToTeam(record),
                show: isAdminOrOwner && filter?.value === 'available',
              },
              {
                label: <Trans i18nKey="snapshot.actionRestore">Restore</Trans>,
                onClick: () => onResumeSnapshot(record.resID),
                show: filter?.value !== 'available',
              },
              {
                label:
                  filter?.value === 'restorable' ? (
                    <Trans i18nKey="snapshot.actionPermanentDelete">Permanently Delete</Trans>
                  ) : (
                    <Trans i18nKey="snapshot.actionDelete">Delete</Trans>
                  ),
                onClick: () => deleteSnapshot(record),
                type: 'danger',
              },
            ]}
          />
        )}
        {!loading && !notAvailable && (
          <div className="flex justify-between items-center mt-4">
            <div className="t-sm text-secondary">
              {selectedIds.length > 0 ? (
                <Trans i18nKey="team.admin.selected" values={{ count: selectedIds.length, total }}>
                  Selected {selectedIds.length} / {total}
                </Trans>
              ) : (
                <span>&nbsp;</span>
              )}
            </div>
            <Pagination
              size={PAGE_SIZE}
              total={total}
              current={page}
              onChange={(page) => getSnapshotList(page)}
            />
          </div>
        )}
        {exchargeModalOpen && (
          <ExchargeModal
            onOk={() => {
              setExchargeModalOpen(false)
              userStore.getAccount()
              getSnapshotList()
              getItemConsume()
            }}
            onClose={() => {
              setExchargeModalOpen(false)
            }}
          />
        )}

        {!loading && notAvailable && (
          <div className="mt-4 mb-2 ">
            <EmptyView
              title={
                <Trans i18nKey="snap.insufficientQuotaAvailableTitle">
                  Insufficient quota available
                </Trans>
              }
              content={
                <Trans i18nKey="snap.insufficientQuotaAvailableContent">
                  Update your quota settings in Space Management to proceed
                </Trans>
              }
              action={
                <Button type="secondary" onClick={() => navigate('/team/space/member')}>
                  <Trans i18nKey="snapshots.emptyViewActionBtn">Manage Space</Trans>
                </Button>
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}
